/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';
import download from 'downloadjs';
import swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Loading from '../../components/common/Loading';
import Pagination from 'react-bootstrap/Pagination';
import OrderDetailModal from '../../components/order/OrderDetailModal';
import PdfPreviewModal from '../../components/order/PdfPreviewModal';

/* ─── animations ─── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const toastFade = keyframes`
  0%   { opacity: 0; transform: translate(-50%, 4px); }
  15%  { opacity: 1; transform: translate(-50%, 0); }
  85%  { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -4px); }
`;

/* ─── layout ─── */
const Container = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 94vh;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  margin: 16px 16px 16px 0;
  padding: 28px 28px 28px 28px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

const Toolbar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchInput = styled.input`
  padding: 7px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  width: 200px;
  outline: none;
  transition: border-color 0.15s;
  &:focus {
    border-color: #007aff;
  }
`;

const Select = styled.select`
  padding: 7px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s;
  &:focus {
    border-color: #007aff;
  }
`;

const Btn = styled.button`
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
  white-space: nowrap;
  background: ${({ $variant }) => ($variant === 'primary' ? '#007aff' : '#f3f4f6')};
  color: ${({ $variant }) => ($variant === 'primary' ? '#fff' : '#374151')};
  &:hover {
    background: ${({ $variant }) => ($variant === 'primary' ? '#0066d6' : '#e5e7eb')};
  }
`;

const TotalText = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

/* ─── table ─── */
const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Th = styled.th`
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: #2d3748;
  border-bottom: none;
  text-align: ${({ $align }) => $align || 'left'};
  white-space: nowrap;

  &:first-child { border-radius: 8px 0 0 0; }
  &:last-child  { border-radius: 0 8px 0 0; }
`;

const Tr = styled.tr`
  animation: ${fadeIn} 0.2s ease;
  transition: background 0.1s;
  cursor: pointer;

  &:nth-child(even) {
    background: rgba(0, 0, 0, 0.015);
  }
  &:hover {
    background: rgba(0, 122, 255, 0.04);
  }
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 15px;
  color: #111827;
  border-bottom: 1px solid #f3f4f6;
  text-align: ${({ $align }) => $align || 'left'};
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ─── order id chip ─── */
const OrderIdChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: rgba(0, 122, 255, 0.08);
  color: #007aff;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'SF Mono', 'Menlo', monospace;
  cursor: pointer;
  position: relative;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 122, 255, 0.14);
  }
`;

const CopyIcon = styled.span`
  display: inline-flex;
  opacity: 0.5;
  font-size: 11px;
`;

/* ─── tooltip ─── */
const TooltipWrap = styled.span`
  position: relative;
  cursor: default;

  &::after {
    content: attr(data-tip);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 12px;
    background: #1f2937;
    color: #fff;
    font-size: 13px;
    border-radius: 8px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }

  &:hover::after {
    opacity: 1;
  }
`;

/* ─── toast notification ─── */
const Toast = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  background: #1f2937;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  z-index: 9999;
  animation: ${toastFade} 1.6s ease forwards;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

/* ─── tracking cell ─── */
const TrackingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $pending }) =>
    $pending
      ? css`
          background: #fef3c7;
          color: #92400e;
        `
      : css`
          background: #ecfdf5;
          color: #065f46;
        `}
`;

const EditBtn = styled.button`
  padding: 3px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #374151;
  }
`;

/* ─── download button in table ─── */
const DlBtn = styled.button`
  padding: 4px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #007aff;
    color: #fff;
    border-color: #007aff;
  }
`;

/* ─── status badge ─── */
const StatusDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 4px;
  background: ${({ $shipped }) => ($shipped ? '#10b981' : '#f59e0b')};
`;

/* ─── empty state ─── */
const EmptyState = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #9ca3af;
  font-size: 15px;
`;

/* ─── column widths ─── */
const COL_WIDTHS = ['10%', '13%', '5%', '22%', '8%', '16%', '9%', '5%'];

/* ─── constants ─── */
const PAGE_SIZES = [20, 30, 50, 100];
const STORAGE_KEY = 'orderlist_pagesize';

/* ─── helpers ─── */
const shortenOrderId = (id) => {
  if (!id || id.length <= 10) return id;
  // "260408_002_222000" -> "260408_002"
  const parts = id.split('_');
  if (parts.length >= 2) return `${parts[0]}_${parts[1]}`;
  return id.substring(0, 10);
};

const formatDateCompact = (dateStr) => {
  if (!dateStr) return '-';
  // "2026.04.08-09:22:31" -> "04/08"
  const match = dateStr.match(/(\d{4})\.(\d{2})\.(\d{2})/);
  if (match) return `${match[2]}/${match[3]}`;
  return dateStr;
};

const formatDateFull = (dateStr) => {
  if (!dateStr) return '-';
  // "2026.04.08-09:22:31" -> "26/04/08 09:22:31"
  const match = dateStr.match(/(\d{4})\.(\d{2})\.(\d{2})-?(\d{2}):(\d{2}):(\d{2})/);
  if (match) return `${match[1].slice(2)}/${match[2]}/${match[3]} ${match[4]}:${match[5]}:${match[6]}`;
  const dateOnly = dateStr.match(/(\d{4})\.(\d{2})\.(\d{2})/);
  if (dateOnly) return `${dateOnly[1].slice(2)}/${dateOnly[2]}/${dateOnly[3]}`;
  return dateStr;
};

const parseTracking = (raw) => {
  if (!raw || raw === '발주 확인 중') {
    return { isPending: true, display: '확인 중', full: raw || '발주 확인 중' };
  }
  // "롯데택배 249488033395 / 황진영" -> carrier + partial number
  const parts = raw.split(/\s+/);
  const carrier = parts[0] || '';
  const number = parts[1] || '';
  const shortNum = number.length > 6 ? number.substring(0, 6) + '..' : number;
  return {
    isPending: false,
    display: `${carrier} ${shortNum}`,
    full: raw,
  };
};

/* ═══════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════ */
const OrderListPage = () => {
  const [searchParams] = useSearchParams();
  const urlFrom = searchParams.get('from') || '';
  const urlTo = searchParams.get('to') || '';
  const urlSearch = searchParams.get('q') || '';

  const [orders, setOrders] = useState(null);
  const [rawOrders, setRawOrders] = useState([]);
  const [orderIds, setOrderIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 20;
  });
  const [search, setSearch] = useState(urlSearch);
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [status, setStatus] = useState('');
  const [datePeriod, setDatePeriod] = useState(urlFrom ? 'custom' : '');
  const [dateFrom, setDateFrom] = useState(urlFrom);
  const [dateTo, setDateTo] = useState(urlTo);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  /* ─── toast helper ─── */
  const showToast = (msg) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 1600);
  };

  /* ─── clipboard copy ─── */
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('발주번호가 복사되었습니다');
    } catch {
      showToast('복사에 실패했습니다');
    }
  };

  /* ─── date period helpers ─── */
  const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${dd}`;
  };

  const applyDatePeriod = (period) => {
    setDatePeriod(period);
    const now = new Date();
    let from = '';
    let to = toDateStr(now);

    if (period === 'today') {
      from = toDateStr(now);
    } else if (period === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      from = toDateStr(y);
    } else if (period === 'week') {
      const w = new Date(now);
      const day = w.getDay();
      w.setDate(w.getDate() - (day === 0 ? 6 : day - 1));
      from = toDateStr(w);
    } else if (period === 'month') {
      from = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.01`;
    } else if (period === 'lastmonth') {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      from = toDateStr(lm);
      const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      to = toDateStr(lmEnd);
    } else {
      // 'custom' or '' → don't auto-set
      return;
    }

    setDateFrom(from);
    setDateTo(to);
    setPage(1);
    fetchOrders(1, pageSize, search, status, from, to);
  };

  const handleCustomDateChange = (f, t) => {
    setDateFrom(f);
    setDateTo(t);
    setDatePeriod('custom');
    setPage(1);
    fetchOrders(1, pageSize, search, status, f, t);
  };

  /* ─── fetch orders ─── */
  const fetchOrders = useCallback(async (pageNum, size, searchVal, statusVal, from, to) => {
    const p = pageNum || page;
    const s = size || pageSize;
    const q = searchVal !== undefined ? searchVal : search;
    const st = statusVal !== undefined ? statusVal : status;
    const f = from !== undefined ? from : dateFrom;
    const t = to !== undefined ? to : dateTo;
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.set('size', s);
      if (q) params.set('search', q);
      if (st) params.set('status', st);
      if (f) params.set('from', f);
      if (t) params.set('to', t);

      const { data: res } = await axios.get(`/admin/users/orderlist/${p}?${params.toString()}`);
      const ids = res.id.map((item) => item.id);

      setOrderIds(ids);
      setRawOrders(
        res.data.map((r, i) => ({
          ...r,
          _docId: res.id[i].id,
          _rawPostNum: r.postNum,
        }))
      );
      setOrders(res.data);
      setTotalPages(res.limit);
      setTotalCount(res.total || 0);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, status, dateFrom, dateTo]);

  /* ─── view & edit tracking ─── */
  const openTracking = async ({ id, num }) => {
    const result = await swal.fire({
      title: '운송장 정보',
      input: 'textarea',
      inputValue: num || '',
      inputAttributes: {
        style: 'font-size:14px; line-height:1.6; min-height:80px;',
      },
      confirmButtonText: '저장',
      cancelButtonText: '닫기',
      confirmButtonColor: '#007aff',
      cancelButtonColor: '#a3a3a3',
      showCancelButton: true,
      width: 520,
    });
    if (result.isConfirmed && result.value !== num) {
      try {
        const res = await axios.patch(`/admin/users/orderlist/${id}`, {
          postNum: result.value,
        });
        if (res.status === 200) {
          swal.fire({
            text: '변경이 완료되었습니다.',
            icon: 'success',
            showConfirmButton: false,
            timer: 1000,
          });
          fetchOrders(page, pageSize, search, status, dateFrom, dateTo);
        }
      } catch {
        swal.fire({
          width: 400,
          text: '오류가 발생했습니다.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1000,
        });
      }
    }
  };

  /* ─── download PDF ─── */
  const getUrl = async (orderId, id) => {
    try {
      const { data: res } = await axios.get(`/admin/users/orderlist/download/${id}`);
      download(res, `슈퍼트랙_발주서_${orderId}.pdf`, 'application/pdf');
    } catch (e) {
      console.log(e);
    }
  };

  /* ─── pagination ─── */
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber);
      fetchOrders(pageNumber, pageSize, search, status, dateFrom, dateTo);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    localStorage.setItem(STORAGE_KEY, newSize);
    setPage(1);
    fetchOrders(1, newSize, search, status, dateFrom, dateTo);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
    fetchOrders(1, pageSize, searchInput, status, dateFrom, dateTo);
  };

  const handleStatusChange = (e) => {
    const val = e.target.value;
    setStatus(val);
    setPage(1);
    fetchOrders(1, pageSize, search, val, dateFrom, dateTo);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setStatus('');
    setDatePeriod('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    fetchOrders(1, pageSize, '', '', '', '');
  };

  /* ─── excel download ─── */
  const handleExcelDownload = async () => {
    if (!dateFrom || !dateTo) {
      swal.fire({ text: '엑셀 다운로드는 날짜 필터를 설정해야 합니다.', icon: 'warning', confirmButtonColor: '#007aff' });
      return;
    }
    // 최대 6개월 체크
    const from = new Date(dateFrom.replace(/\./g, '-'));
    const to = new Date(dateTo.replace(/\./g, '-'));
    const diffMs = to - from;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > 186) {
      swal.fire({ text: '최대 6개월까지 다운로드할 수 있습니다.', icon: 'warning', confirmButtonColor: '#007aff' });
      return;
    }

    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.set('from', dateFrom);
      params.set('to', dateTo);
      if (search) params.set('search', search);
      if (status) params.set('status', status);

      const { data: res } = await axios.get(`/admin/users/orderlist-export?${params.toString()}`);

      const rows = res.rows.map((r) => ({
        '발주번호': r.order_id,
        '회사': r.company_name,
        '발주일시': r.time,
        '상품명': r.product_name,
        '수량': r.count,
        '단가': r.unit_price,
        '상품금액': r.item_total,
        '주문총액': r.verified_price,
        '운송장': r.postNum,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 22 }, { wch: 18 }, { wch: 20 }, { wch: 35 },
        { wch: 6 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 35 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '발주내역');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], { type: 'application/octet-stream' });
      saveAs(blob, `발주내역_${dateFrom.replace(/\./g, '')}_${dateTo.replace(/\./g, '')}.xlsx`);
    } catch (e) {
      swal.fire({ text: '다운로드 중 오류가 발생했습니다.', icon: 'error', confirmButtonColor: '#007aff' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(1, pageSize, urlSearch, '', urlFrom, urlTo);
  }, []);

  const makePaginationItems = () => {
    const items = [];
    const groupStart = Math.floor((page - 1) / 10) * 10 + 1;
    const groupEnd = Math.min(groupStart + 9, totalPages);
    for (let i = groupStart; i <= groupEnd; i++) {
      items.push(
        <Pagination.Item key={i} active={page === i} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  /* ═══ render ═══ */
  return (
    <Container>
      <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>발주 내역</h2>
        </div>

        {/* toolbar row 1 - search & status */}
        <Toolbar>
          <ToolGroup>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
              <SearchInput
                placeholder="발주번호 / 회사명 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <Btn $variant="primary" type="submit">검색</Btn>
            </form>
            <Select value={status} onChange={handleStatusChange}>
              <option value="">전체 상태</option>
              <option value="pending">발주 확인 중</option>
              <option value="shipped">배송 처리됨</option>
            </Select>
            {(search || status || datePeriod) && (
              <Btn onClick={handleReset}>초기화</Btn>
            )}
          </ToolGroup>
          <ToolGroup>
            <Btn
              onClick={handleExcelDownload}
              style={{ background: '#1d6f42', color: '#fff', padding: '7px 14px' }}
            >
              엑셀 다운로드
            </Btn>
            <TotalText>총 {totalCount.toLocaleString()}건</TotalText>
            <Select value={pageSize} onChange={handlePageSizeChange}>
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s}건씩</option>
              ))}
            </Select>
          </ToolGroup>
        </Toolbar>

        {/* toolbar row 2 - date filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { key: 'today', label: '오늘' },
            { key: 'yesterday', label: '오늘+어제' },
            { key: 'week', label: '이번 주' },
            { key: 'month', label: '이번 달' },
            { key: 'lastmonth', label: '지난 달' },
          ].map((btn) => (
            <Btn
              key={btn.key}
              $variant={datePeriod === btn.key ? 'primary' : undefined}
              onClick={() => applyDatePeriod(btn.key)}
              style={{ padding: '5px 12px', fontSize: 13 }}
            >
              {btn.label}
            </Btn>
          ))}
          <span style={{ color: '#9ca3af', fontSize: 13, margin: '0 4px' }}>|</span>
          <input
            type="date"
            value={dateFrom ? dateFrom.replace(/\./g, '-') : ''}
            onChange={(e) => {
              const v = e.target.value.replace(/-/g, '.');
              handleCustomDateChange(v, dateTo || toDateStr(new Date()));
            }}
            style={{ padding: '5px 8px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none' }}
          />
          <span style={{ color: '#9ca3af', fontSize: 13 }}>~</span>
          <input
            type="date"
            value={dateTo ? dateTo.replace(/\./g, '-') : ''}
            onChange={(e) => {
              const v = e.target.value.replace(/-/g, '.');
              handleCustomDateChange(dateFrom || '2020.01.01', v);
            }}
            style={{ padding: '5px 8px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none' }}
          />
          {datePeriod && (
            <TotalText style={{ marginLeft: 4 }}>
              {dateFrom} ~ {dateTo}
            </TotalText>
          )}
        </div>

        {/* table */}
        {rawOrders && rawOrders.length > 0 ? (
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
            <StyledTable>
              <colgroup>
                {COL_WIDTHS.map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <Thead>
                <tr>
                  <Th>발주번호</Th>
                  <Th $align="center" style={{ width: 30 }}></Th>
                  <Th>회사</Th>
                  <Th $align="center">날짜</Th>
                  <Th>상품</Th>
                  <Th $align="right">금액</Th>
                  <Th>운송장</Th>
                  <Th $align="center">상태</Th>
                  <Th $align="center">PDF</Th>
                </tr>
              </Thead>
              <tbody>
                {rawOrders.map((row, idx) => {
                  const tracking = parseTracking(row._rawPostNum);
                  const isShipped = !tracking.isPending;

                  return (
                    <Tr
                      key={idx}
                      onClick={() => setSelectedOrderId(orderIds[idx])}
                    >
                      {/* 발주번호 - shortened, click to copy */}
                      <Td>
                        <OrderIdChip
                          title={row.orderId}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(row.orderId);
                          }}
                        >
                          {shortenOrderId(row.orderId)}
                          <CopyIcon>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                            </svg>
                          </CopyIcon>
                        </OrderIdChip>
                      </Td>

                      {/* 메모 */}
                      <Td $align="center" style={{ padding: '0 4px', width: 30 }}>
                        {row.has_memo && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" title="메모 있음">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        )}
                      </Td>

                      {/* 회사 */}
                      <Td style={{ fontWeight: 700, color: '#111827', whiteSpace: 'normal', wordBreak: 'keep-all' }}>{row.company_name}</Td>

                      {/* 날짜 - compact with tooltip */}
                      <Td $align="center" style={{ overflow: 'visible' }}>
                        <TooltipWrap data-tip={formatDateFull(row.time)}>
                          {formatDateCompact(row.time)}
                        </TooltipWrap>
                      </Td>

                      {/* 상품 */}
                      <Td style={{ fontWeight: 700, color: '#111827', whiteSpace: 'normal', wordBreak: 'keep-all' }}>{row.product}</Td>

                      {/* 금액 */}
                      <Td $align="right" style={{ fontWeight: 600, fontFamily: "'SF Mono', 'Menlo', monospace" }}>
                        {row.price ? row.price.toLocaleString('ko-KR') : '-'}
                      </Td>

                      {/* 운송장 - click to view/edit */}
                      <Td>
                        <TrackingBadge
                          $pending={tracking.isPending}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openTracking({ id: row._docId, num: row._rawPostNum });
                          }}
                        >
                          {tracking.isPending ? (
                            <>
                              <StatusDot $shipped={false} />
                              {tracking.display}
                            </>
                          ) : (
                            tracking.display
                          )}
                        </TrackingBadge>
                      </Td>

                      {/* 상태 */}
                      <Td $align="center">
                        <span style={{ fontSize: 14, fontWeight: 500, color: isShipped ? '#059669' : '#d97706' }}>
                          <StatusDot $shipped={isShipped} />
                          {isShipped ? '출고완료' : '확인중'}
                        </span>
                      </Td>

                      {/* PDF 미리보기 */}
                      <Td $align="center">
                        <DlBtn
                          onClick={(e) => {
                            e.stopPropagation();
                            setPdfPreview({ orderId: row.orderId, docId: row._docId });
                          }}
                        >
                          PDF
                        </DlBtn>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </StyledTable>
          </div>
        ) : (
          !isLoading && (
            <EmptyState>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}>
                <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
                <polyline points="13 2 13 9 20 9" />
              </svg>
              <span>{(search || status) ? '검색 결과가 없습니다.' : '발주 내역이 없습니다.'}</span>
            </EmptyState>
          )
        )}
      </div>

      {/* pagination */}
      {totalPages > 1 && (
        <Pagination style={{ marginTop: 16, marginBottom: 0 }}>
          <Pagination.First onClick={() => handlePageChange(1)} />
          <Pagination.Prev onClick={() => handlePageChange(page - 1)} />
          {makePaginationItems()}
          <Pagination.Next onClick={() => handlePageChange(page + 1)} />
          <Pagination.Last onClick={() => handlePageChange(totalPages)} />
        </Pagination>
      )}

      {/* loading */}
      {isLoading && <Loading />}

      {/* detail modal */}
      {selectedOrderId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onDeleted={() => fetchOrders(page, pageSize, search, status, dateFrom, dateTo)}
        />
      )}

      {/* PDF preview modal */}
      {pdfPreview && (
        <PdfPreviewModal
          orderId={pdfPreview.orderId}
          docId={pdfPreview.docId}
          onClose={() => setPdfPreview(null)}
        />
      )}

      {/* copy toast */}
      {toast && <Toast key={toast + Date.now()}>{toast}</Toast>}
    </Container>
  );
};

export default OrderListPage;
